import type { AggregateAssetsParams } from "@hooks/use-get/aggregate-assets";
import type { DynamicFilterState } from "@hooks/use-report-filter";

/**
 * Converts dynamic filter state into the dynamicFilters parameter for AggregateAssetsParams.
 * 
 * This passes the filter state as-is to the API, which handles all field types generically.
 * Works with any field from enabledFields - no hardcoded parameter mapping needed.
 *
 * @example
 * getAggregateAssets({
 *   groupBy: ["type_asset", "asset_site"],
 *   ...buildFilterParams(filterState),
 * });
 */
export const buildFilterParams = (
  filterState: DynamicFilterState,
  overrides?: Partial<AggregateAssetsParams>,
): Partial<AggregateAssetsParams> => {
  // Pass filter state directly - the API handles all field types generically
  return {
    dynamicFilters: filterState,
    ...overrides,
  };
};

/**
 * Builds query-string params for detail CSV export REST calls,
 * derived from the dynamic filter state.
 *
 * Array fields → "filters[columnName][]": value
 * Date fields  → "filters[columnName][start/end]": value
 */
export const buildGlobalQueryParams = (filterState: DynamicFilterState): Record<string, string> => {
  const params: Record<string, string> = {};

  for (const [columnName, value] of Object.entries(filterState)) {
    if (Array.isArray(value) && value.length > 0) {
      // For multi-value array params, join and store
      const paramKey = `filters[${columnName}][]`;
      params[paramKey] = value.join(",");
    } else if (typeof value === "object" && value !== null && "start" in value && "end" in value) {
      // Date range
      if (value.start) params[`filters[${columnName}][start]`] = value.start;
      if (value.end) params[`filters[${columnName}][end]`] = value.end;
    }
  }

  return params;
};
